import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import FollowUpIcon from 'src/assets/svg/followupinspirations/FollowUpIcon.svg?react';
import history from 'src/services/routing/history';
import { routePaths } from 'src/services/routing/routePaths';
import { useUserContext } from 'src/components/app/context/UserContext';

import { styles } from './index.styles';

export const FOLLOW_UP_INSPIRATIONS_BUTTON_TEST_ID = 'follow-up-button';
export const FOLLOW_UP_HOVER_TEXT_TEST_ID = 'follow-up-hover-text';

const FollowUpInspirationsButton = () => {
  const [pulseAnimation, setPulseAnimation] = useState(true);
  const [clicked, setClicked] = useState(false);
  const { t } = useTranslation('followUpInspirations');
  const theme = useTheme();
  const upLg = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const href = history.createHref({
    pathname: routePaths.FOLLOW_UP_INSPIRATIONS,
  });
  const { user } = useUserContext();

  useEffect(() => {
    const timer = setTimeout(() => {
      setPulseAnimation(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event: TouchEvent) => {
      if (!buttonRef.current?.contains(event.target as Node)) {
        setClicked(false);
      }
    };
    document.addEventListener('touchstart', handleOutsideClick);
    return () => {
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [upLg]);

  const handleClick = () => {
    if (!upLg && !clicked) {
      setClicked(true);
      setPulseAnimation(false);
    } else {
      window.open(href, '_blank');
    }
  };

  if (!user) {
    return null;
  }

  return (
    <Button
      ref={buttonRef}
      variant="cta"
      sx={styles.followUpInspirationsButton(pulseAnimation, clicked)}
      onMouseOver={() => upLg && setPulseAnimation(false)}
      onClick={handleClick}
      data-test-id={FOLLOW_UP_INSPIRATIONS_BUTTON_TEST_ID}
    >
      <FollowUpIcon style={styles.followUpIcon} />
      <Box sx={styles.buttonTextContainer} className="fui-banner-content">
        <Box sx={styles.buttonText}>
          <Typography
            variant="button2"
            data-test-id={FOLLOW_UP_HOVER_TEXT_TEST_ID}
          >
            {upLg ? t('Button.label.desktop') : t('Button.label.mobile')}
          </Typography>
        </Box>
      </Box>
    </Button>
  );
};

export default FollowUpInspirationsButton;
